import type { FieldPath, UseFormSetError } from 'react-hook-form';
import type { ToastProps } from '@hopskipdrive/react-components';

import type { ValidationErrors } from '../../../../api/services/Riders';
import type { RiderFormValues } from '../types';

import { isServiceConflictMessage } from './parseServiceConflicts';

type SetError = UseFormSetError<RiderFormValues>;
type AddToast = (toast: ToastProps) => void;
type SetServiceConflictMessages = (messages: string[]) => void;

// Order matters: more specific patterns must be checked before their broader prefixes
// (e.g. "residence account contact email" before "residence account").
const SHARED_BILLING_RIDER_FIELD_PATTERNS: Array<[RegExp, string]> = [
  [/^residence account contact email/i, 'residenceAccountContactEmail'],
  [/^residence account contact name/i, 'residenceAccountContactName'],
  [/^residence account/i, 'residenceAccountId'],
];

export const mapApiErrorsToForm = (
  responseData: Record<string, unknown> | undefined,
  setError: SetError,
  addToast: AddToast,
  setServiceConflictMessages?: SetServiceConflictMessages
): void => {
  if (!responseData) {
    return;
  }

  const errors = responseData.errors as ValidationErrors | string[] | undefined;

  if (!errors) {
    return;
  }

  // Reset first so a stale conflict highlight doesn't linger once a later response no longer
  // reports one; the array branch below overwrites this if it finds a fresh message.
  setServiceConflictMessages?.([]);

  if (Array.isArray(errors)) {
    const dobMessages: string[] = [];
    const unmatched: string[] = [];
    const serviceConflictMessages: string[] = [];

    for (const message of errors) {
      if (isServiceConflictMessage(message)) {
        serviceConflictMessages.push(message);
      } else if (/first name/i.test(message)) {
        setError('firstName', { message });
      } else if (/last name/i.test(message)) {
        setError('lastName', { message });
      } else if (/date of birth|age must be/i.test(message)) {
        dobMessages.push(message);
      } else if (/^SharedBillingRider errors: Validation failed: /i.test(message)) {
        // The shared billing save can fail validations the pre-check doesn't cover
        // (attribute mismatch between the validator's pre-check and the actual save),
        // leaking a raw "SharedBillingRider errors: Validation failed: <detail>" string.
        // Strip the wrapper and route by the remaining humanized attribute name.
        const detail = message.replace(/^SharedBillingRider errors: Validation failed: /i, '');
        const fieldMatch = SHARED_BILLING_RIDER_FIELD_PATTERNS.find(([pattern]) => pattern.test(detail));

        if (fieldMatch) {
          setError(`sharedBillingRider.${fieldMatch[1]}` as FieldPath<RiderFormValues>, { message: detail });
        } else {
          unmatched.push(detail);
        }
      } else if (/email/i.test(message) && /contact error/i.test(message)) {
        // Passenger create validates contacts after the rider record, so a bad contact
        // email surfaces here as a flat "Email Contact Error: ..." message rather than
        // the structured `passengerContact.email` shape /passengers/validate returns.
        setError('contacts.0.email' as FieldPath<RiderFormValues>, { message });
      } else if (/email/i.test(message)) {
        // The rider's own email attribute fails the same way — a plain "Email is
        // invalid"/"Email has already been taken" string with no structured shape.
        setError('email', { message });
      } else {
        unmatched.push(message);
      }
    }

    if (dobMessages.length > 0) {
      setError('dateOfBirth', { message: dobMessages[0] });
    }

    unmatched.forEach((message) => addToast({ severity: 'error', message }));

    if (serviceConflictMessages.length > 0) {
      setServiceConflictMessages?.(serviceConflictMessages);
    }

    return;
  }

  for (const key of Object.keys(errors) as (keyof ValidationErrors)[]) {
    switch (key) {
      case 'contactError': {
        const { fullName, phoneNumber, dateOfBirth } = errors.contactError ?? {};

        if (fullName) {
          setError('contacts.0.firstName' as FieldPath<RiderFormValues>, { message: fullName });
          setError('contacts.0.lastName' as FieldPath<RiderFormValues>, { message: fullName });
        }
        if (phoneNumber) {
          setError('contacts.0.phoneNumber' as FieldPath<RiderFormValues>, { message: phoneNumber });
        }
        if (dateOfBirth) {
          setError('dateOfBirth', { message: dateOfBirth });
        }
        break;
      }
      case 'passengerContact': {
        const { fullName, role, relationship, email } = errors.passengerContact ?? {};

        if (fullName) {
          setError('contacts.0.firstName' as FieldPath<RiderFormValues>, { message: fullName });
          setError('contacts.0.lastName' as FieldPath<RiderFormValues>, { message: fullName });
        }
        if (role) {
          setError('contacts.0.role' as FieldPath<RiderFormValues>, { message: role });
        }
        if (relationship) {
          setError('contacts.0.relationship' as FieldPath<RiderFormValues>, { message: relationship });
        }
        if (email) {
          setError('contacts.0.email' as FieldPath<RiderFormValues>, { message: email });
        }
        break;
      }
      case 'email':
        setError('email', { message: errors.email });
        break;
      case 'gender':
        setError('gender', { message: errors.gender });
        break;
      case 'codeWord':
        setError('codeWord', { message: errors.codeWord });
        break;
      case 'profilePhoto':
        setError('photosAttributes.0.attachment' as FieldPath<RiderFormValues>, { message: errors.profilePhoto });
        break;
      case 'sharedBillingRider': {
        const sharedErrors = errors.sharedBillingRider ?? {};

        for (const field of Object.keys(sharedErrors) as (keyof typeof sharedErrors)[]) {
          setError(`sharedBillingRider.${field}` as FieldPath<RiderFormValues>, { message: sharedErrors[field] });
        }
        break;
      }
      default: {
        const message = (errors as Record<string, unknown>)[key];

        if (typeof message === 'string') {
          addToast({ severity: 'error', message });
        }
      }
    }
  }
};
